import type {
  ConflictGroupSummary,
  QboTransactionSearchRow,
} from '@/services/qboTransactionSearch'
import {
  descriptionIgnoreKey,
  normalizeLineDescription,
} from '@/utils/normalizeLineDescription'

export interface CategoryConflictIgnoreSets {
  ignoredGroupIds: ReadonlySet<string>
  ignoredDescriptionKeys: ReadonlySet<string>
}

export function buildCategoryConflictIgnoreSets(
  ignoredGroupIds: string[],
  ignoredDescriptionEntries: { contact_name: string; normalized_description: string }[]
): CategoryConflictIgnoreSets {
  const groupIds = new Set<string>()
  for (const id of ignoredGroupIds) {
    const trimmed = id.trim()
    if (trimmed) groupIds.add(trimmed)
  }

  const descriptionKeys = new Set<string>()
  for (const entry of ignoredDescriptionEntries) {
    const contact = entry.contact_name.trim()
    const normalized = entry.normalized_description.trim()
    if (!contact || !normalized) continue
    descriptionKeys.add(descriptionIgnoreKey(contact, normalized))
  }

  return { ignoredGroupIds: groupIds, ignoredDescriptionKeys: descriptionKeys }
}

/**
 * Remove rows belonging to ignored groups or matching ignored vendor+description keys.
 */
export function filterIgnoredConflictRows(
  rows: QboTransactionSearchRow[],
  ignores: CategoryConflictIgnoreSets
): QboTransactionSearchRow[] {
  if (ignores.ignoredGroupIds.size === 0 && ignores.ignoredDescriptionKeys.size === 0) {
    return rows
  }

  return rows.filter((row) => {
    const groupId = row.conflict_group_id
    if (groupId && ignores.ignoredGroupIds.has(groupId)) return false

    const contact = (row.contact_name || '').trim()
    const normalized = normalizeLineDescription(row.line_item_description || '')
    if (!contact || !normalized) return true

    const key = descriptionIgnoreKey(contact, normalized)
    return !ignores.ignoredDescriptionKeys.has(key)
  })
}

export interface IgnoredDescriptionEntryInput {
  contact_name: string
  normalized_description: string
  display_description: string
}

/** One ignore entry per distinct vendor + normalized description in the selection. */
export function buildIgnoredDescriptionEntriesFromRows(
  rows: QboTransactionSearchRow[]
): IgnoredDescriptionEntryInput[] {
  const seen = new Set<string>()
  const entries: IgnoredDescriptionEntryInput[] = []

  for (const row of rows) {
    const contact = (row.contact_name || '').trim()
    const rawDesc = (row.line_item_description || '').trim()
    const normalized = normalizeLineDescription(rawDesc)
    if (!contact || !normalized) continue

    const key = descriptionIgnoreKey(contact, normalized)
    if (seen.has(key)) continue
    seen.add(key)

    entries.push({
      contact_name: contact,
      normalized_description: normalized,
      display_description: rawDesc || normalized,
    })
  }

  return entries
}

export interface ConflictCategoryChip {
  name: string
  visibleCount: number
  totalCount: number
}

export interface ConflictGroupView {
  groupId: string
  contactName: string
  representativeDescription: string
  categories: ConflictCategoryChip[]
  visibleRowCount: number
  totalRowCount: number
}

function toLocalIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Keys between anchor and target in a visible row order (inclusive).
 * Returns [] when either key is missing from orderedKeys.
 */
export function keysInVisibleRange(
  orderedKeys: string[],
  anchorKey: string,
  targetKey: string
): string[] {
  const anchorIdx = orderedKeys.indexOf(anchorKey)
  const targetIdx = orderedKeys.indexOf(targetKey)
  if (anchorIdx === -1 || targetIdx === -1) return []
  const lo = Math.min(anchorIdx, targetIdx)
  const hi = Math.max(anchorIdx, targetIdx)
  return orderedKeys.slice(lo, hi + 1)
}

/** Trailing 12 months: end = today, start = same calendar day one year earlier (ISO yyyy-mm-dd). */
export function getTrailingTwelveMonthsDateRange(): { start: string; end: string } {
  const end = new Date()
  const start = new Date(end)
  start.setFullYear(start.getFullYear() - 1)
  return { start: toLocalIsoDate(start), end: toLocalIsoDate(end) }
}

/** Default within-group order: category asc, then date desc */
export function defaultConflictRowCompare(
  a: QboTransactionSearchRow,
  b: QboTransactionSearchRow
): number {
  const catCmp = (a.line_item_category || '').localeCompare(b.line_item_category || '', undefined, {
    sensitivity: 'base',
  })
  if (catCmp !== 0) return catCmp
  return (b.date || '').localeCompare(a.date || '')
}

/**
 * After useSortableTable sorted the flat list, re-bucket by conflict_group_id
 * using groupOrder for display order while preserving row order within each group.
 */
export function bucketRowsByConflictGroup(
  sortedRows: QboTransactionSearchRow[],
  groupOrder: string[]
): { groupId: string; rows: QboTransactionSearchRow[] }[] {
  const buckets = new Map<string, QboTransactionSearchRow[]>()
  for (const row of sortedRows) {
    const gid = row.conflict_group_id
    if (!gid) continue
    if (!buckets.has(gid)) buckets.set(gid, [])
    buckets.get(gid)!.push(row)
  }

  const result: { groupId: string; rows: QboTransactionSearchRow[] }[] = []
  const seen = new Set<string>()

  for (const groupId of groupOrder) {
    const rows = buckets.get(groupId)
    if (rows && rows.length > 0) {
      result.push({ groupId, rows })
      seen.add(groupId)
    }
  }

  for (const [groupId, rows] of buckets) {
    if (!seen.has(groupId) && rows.length > 0) {
      result.push({ groupId, rows })
    }
  }

  return result
}

/**
 * Build display model for group headers given full ConflictGroupSummary[]
 * and currently visible (refined) rows.
 */
export function buildConflictGroupViews(
  conflictGroups: ConflictGroupSummary[],
  visibleRows: QboTransactionSearchRow[]
): ConflictGroupView[] {
  const rowsByGroup = new Map<string, QboTransactionSearchRow[]>()
  for (const row of visibleRows) {
    const gid = row.conflict_group_id
    if (!gid) continue
    const list = rowsByGroup.get(gid) ?? []
    list.push(row)
    rowsByGroup.set(gid, list)
  }

  const views: ConflictGroupView[] = []

  for (const group of conflictGroups) {
    const visible = rowsByGroup.get(group.group_id) ?? []
    if (visible.length === 0) continue

    const categoryCounts = new Map<string, number>()
    for (const row of visible) {
      const cat = effectiveLineCategory(row) || '(No category)'
      categoryCounts.set(cat, (categoryCounts.get(cat) ?? 0) + 1)
    }

    const totalByCategory = new Map(group.categories.map((c) => [c.name, c.row_count]))

    const categories: ConflictCategoryChip[] = [...categoryCounts.entries()]
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(([name, visibleCount]) => ({
        name,
        visibleCount,
        totalCount: totalByCategory.get(name) ?? visibleCount,
      }))

    views.push({
      groupId: group.group_id,
      contactName: group.contact_name,
      representativeDescription: group.representative_description,
      categories,
      visibleRowCount: visible.length,
      totalRowCount: group.row_count,
    })
  }

  return views
}

export function formatConflictCategoryChip(chip: ConflictCategoryChip): string {
  const count =
    chip.visibleCount < chip.totalCount
      ? `${chip.visibleCount}/${chip.totalCount}`
      : String(chip.visibleCount)
  return `${chip.name} ×${count}`
}

export function formatConflictGroupLineCount(
  visibleRowCount: number,
  totalRowCount: number
): string {
  if (visibleRowCount < totalRowCount) {
    return `${visibleRowCount}/${totalRowCount} lines`
  }
  return `${visibleRowCount} line${visibleRowCount !== 1 ? 's' : ''}`
}

/** Effective category after pending New category edits. */
export function effectiveLineCategory(row: QboTransactionSearchRow): string {
  const pending = row.line_item_category_new?.trim()
  if (pending) return pending
  return (row.line_item_category || '').trim()
}

const CONFLICT_TODO_DONE_STATUSES = new Set([
  'Posted',
  'Dry run OK',
  'Dry run: no change',
  'Dry run: no match',
  'No change',
  'No match',
])

/** True when apply finished successfully for this row — drop from conflict to-do list. */
export function isConflictTodoComplete(row: QboTransactionSearchRow): boolean {
  const status = (row.update_status || '').trim()
  if (!status || /^Error:/i.test(status)) return false
  return CONFLICT_TODO_DONE_STATUSES.has(status)
}

/**
 * In conflict mode, drop rows that are done (Posted / dry-run OK / etc.).
 * Among remaining rows, drop groups that no longer span ≥2 live categories
 * (pending New category edits do not hide rows until Apply succeeds).
 */
export function filterResolvedConflictRows(
  rows: QboTransactionSearchRow[]
): QboTransactionSearchRow[] {
  const activeRows = rows.filter((row) => !isConflictTodoComplete(row))

  const byGroup = new Map<string, QboTransactionSearchRow[]>()
  const ungrouped: QboTransactionSearchRow[] = []

  for (const row of activeRows) {
    const gid = row.conflict_group_id
    if (!gid) {
      ungrouped.push(row)
      continue
    }
    const list = byGroup.get(gid) ?? []
    list.push(row)
    byGroup.set(gid, list)
  }

  const open: QboTransactionSearchRow[] = [...ungrouped]
  for (const groupRows of byGroup.values()) {
    const categories = new Set(
      groupRows
        .map((row) => (row.line_item_category || '').trim())
        .filter((name) => name.length > 0)
    )
    if (categories.size >= 2) {
      open.push(...groupRows)
    }
  }
  return open
}

/** Fold successful *NEW edits into live fields after apply. */
export function applySuccessfulEditsToRow(
  row: QboTransactionSearchRow,
  status: string
): QboTransactionSearchRow {
  const next: QboTransactionSearchRow = { ...row, update_status: status }
  if (!CONFLICT_TODO_DONE_STATUSES.has(status.trim())) {
    return next
  }
  if (row.line_item_category_new?.trim()) {
    next.line_item_category = row.line_item_category_new.trim()
    next.line_item_category_new = undefined
  }
  if (row.line_item_description_new?.trim()) {
    next.line_item_description = row.line_item_description_new.trim()
    next.line_item_description_new = undefined
  }
  if (row.line_item_location_new !== undefined) {
    next.line_item_location = row.line_item_location_new
    next.line_item_location_new = undefined
  }
  if (row.doc_number_new !== undefined) {
    next.doc_number = row.doc_number_new
    next.doc_number_new = undefined
  }
  return next
}

function rowHadPendingApplyEdits(row: QboTransactionSearchRow): boolean {
  return Boolean(
    row.line_item_category_new ||
      row.line_item_description_new ||
      row.line_item_location_new !== undefined ||
      row.doc_number_new !== undefined
  )
}

/**
 * Merge apply job statuses into result rows. Only stamps status onto rows that had
 * pending *NEW edits (avoids marking sibling lines on the same txn as done).
 */
export function mergeApplyStatusesIntoRows(
  rows: QboTransactionSearchRow[],
  statuses: Record<string, string>
): QboTransactionSearchRow[] {
  return rows.map((row) => {
    const status = statuses[row.transaction_id]
    if (!status || !rowHadPendingApplyEdits(row)) return row
    return applySuccessfulEditsToRow(row, status)
  })
}
